/**
 * The body of `/contact-us` (`pages/contact-us/index.tsx`): the title with the brand's phone
 * number (`Helpers/ContactUsPage.Components.tsx` → `ContactUsTitle`), the contact form
 * (`components/ContactFormWithFileAttacher/index.tsx`) in the wide column, and in the narrow
 * column two `components/Shared/YourMetalExperts/YourMetalExperts.tsx` blocks: the metals expert
 * card (`contact_us`) and the plant finder (`contact_us_plant`). Both use `locationsWithExperts`,
 * which the page fetches from Contentful at build time.
 *
 * The form gets no fixed topic (`AllFormTopics.empty`), so a link such as
 * `/contact-us#quoteRequest` preselects one.
 */
import React from "react";
import { useTheme } from "@ryerson/frontend.theme";
import { ContentSection, Grid, Row, Column, Flex } from "@ryerson/frontend.layout";

import YourMetalExperts, {
	YourMetalExpertsProps,
} from "@components/Shared/YourMetalExperts/YourMetalExperts";

import { ContactUsTitle } from "./Helpers/ContactUsPage.Components";
import { ContactFormWithFileAttacher } from "@components/ContactFormWithFileAttacher";
import { AllFormTopics } from "@components/ContactFormWithFileAttacher/ContactFormWithFile.Models";
import { css } from "@emotion/react";
import { Media } from "@ryerson/frontend.next";

/**
 * Lays out the contact-us page; see the file header. `variant` in the props is ignored.
 * Above the `lg` breakpoint the side column keeps a 345 px minimum width.
 */
export const ContactUsPage: React.FC<YourMetalExpertsProps> = ({ locationsWithExperts }) => {
	const { theme } = useTheme();
	const { lighterGray, lightGray } = theme.colors.primary;

	const splitColors = (color: string) => [
		{
			color,
			percentage: "50%",
		},
	];

	const ContactUsSection = () => (
		<Flex
			direction="column"
			css={css`
				gap: 8px;
			`}
		>
			<ContentSection
				type="split"
				split={{
					splitColors: splitColors(lightGray),
					splitDirection: "vertical",
				}}
			>
				<YourMetalExperts
					variant="contact_us"
					locationsWithExperts={locationsWithExperts}
				/>
			</ContentSection>
			<ContentSection type="primary">
				<YourMetalExperts
					variant="contact_us_plant"
					locationsWithExperts={locationsWithExperts}
				/>
			</ContentSection>
		</Flex>
	);

	return (
		<ContentSection type="tertiary" hPadding="50px">
			<ContactUsTitle />
			<Grid gutter={4}>
				<Row>
					<Column lg={8}>
						<ContentSection
							type="split"
							split={{
								splitColors: splitColors(lighterGray),
								splitDirection: "vertical",
							}}
						>
							<div
								css={css`
									padding: 0px 20px 0px 20px;
									@media only screen and (max-width: ${theme.breakpoints.lg}) {
										padding: 0px;
									}
								`}
							>
								<ContactFormWithFileAttacher
									fileRequired={false}
									topic={AllFormTopics.empty}
								/>
							</div>
						</ContentSection>
					</Column>
					<Column lg={4}>
						<Media greaterThanOrEqual="lg">
							<Grid
								gutter={4}
								// set mid width to prevent squishing too much on smaller screens
								style={css`
									min-width: 345px;
								`}
							>
								<Row>
									<Column xl={12}>
										<ContactUsSection />
									</Column>
								</Row>
							</Grid>
						</Media>
						<Media lessThan="lg">
							<Grid gutter={4}>
								<Row>
									<Column xl={12}>
										<ContactUsSection />
									</Column>
								</Row>
							</Grid>
						</Media>
					</Column>
				</Row>
			</Grid>
		</ContentSection>
	);
};
